import React, { useMemo, useState } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Database, Search, Layers, Network } from "lucide-react";

/**
 * MongoDB live snapshot panel for Super Admin → System.
 * Receives the payload from `GET /api/admin/database-stats`.
 */

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0 || bytes == null) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

const KV = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/40 last:border-b-0">
    <span className="text-xs text-muted-foreground whitespace-nowrap">{label}</span>
    <span className={`text-xs text-foreground text-right break-all ${mono ? "font-mono" : ""}`}>
      {value == null || value === "" ? <span className="text-muted-foreground/50">—</span> : value}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, iconClass, title, accent, children, testId, headerExtra }) => (
  <Card className="glass-card border-theme overflow-hidden" data-testid={testId}>
    <div className={`px-4 py-2.5 border-b border-border/50 flex items-center gap-2 ${accent || "bg-muted/30"}`}>
      {Icon && <Icon className={`w-4 h-4 ${iconClass || "text-foreground"}`} />}
      <h3 className="text-sm font-semibold text-foreground font-['Manrope']">{title}</h3>
      {headerExtra}
    </div>
    <CardContent className="p-4 space-y-0.5">{children}</CardContent>
  </Card>
);

const DatabaseInfoPanel = ({ dbStats }) => {
  const [collFilter, setCollFilter] = useState("");

  const sortedCollections = useMemo(() => {
    const list = (dbStats?.collections || []).slice();
    list.sort((a, b) => (b.size || 0) - (a.size || 0));
    if (collFilter) {
      const q = collFilter.toLowerCase();
      return list.filter((c) => c.name.toLowerCase().includes(q));
    }
    return list;
  }, [dbStats, collFilter]);

  if (!dbStats) {
    return (
      <Card className="glass-card border-theme">
        <CardContent className="p-12 text-center text-muted-foreground text-sm">
          Loading database snapshot…
        </CardContent>
      </Card>
    );
  }

  const ds = dbStats.db_stats || {};
  const ss = dbStats.server_status || {};
  const host = dbStats.host || {};

  return (
    <div className="space-y-4" data-testid="database-info-panel">
      {/* Quick read */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="glass-card border-theme">
          <CardContent className="p-3.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Documents</div>
            <div className="text-xl font-bold text-foreground tabular-nums">{(ds.objects || 0).toLocaleString()}</div>
            <div className="text-[10px] text-muted-foreground">avg {formatBytes(ds.avg_obj_size)}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-3.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Data Size</div>
            <div className="text-xl font-bold text-foreground tabular-nums">{formatBytes(ds.data_size)}</div>
            <div className="text-[10px] text-muted-foreground">storage {formatBytes(ds.storage_size)}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-3.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Indexes</div>
            <div className="text-xl font-bold text-foreground tabular-nums">{ds.indexes || 0}</div>
            <div className="text-[10px] text-muted-foreground">{formatBytes(ds.index_size)}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-3.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Connections</div>
            <div className="text-xl font-bold text-foreground tabular-nums">{ss.connections?.current ?? "—"}</div>
            <div className="text-[10px] text-muted-foreground">
              {ss.connections?.available != null ? `${ss.connections.available} available` : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Server Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="MongoDB Server" testId="db-server-card" accent="bg-emerald-500/10" icon={Database} iconClass="text-emerald-500">
          <KV label="Version" value={dbStats.version} mono />
          <KV label="Git Version" value={dbStats.git_version ? dbStats.git_version.slice(0, 14) + "…" : null} mono />
          <KV label="Storage Engine" value={ss.storage_engine} mono />
          <KV label="Database Name" value={ds.db} mono />
          <KV label="Collections" value={ds.collections} />
          <KV label="Views" value={ds.views} />
          <KV label="Server Uptime" value={ss.uptime_human} mono />
          <KV label="Server Time" value={ss.local_time && new Date(ss.local_time).toLocaleString()} />
          <KV label="Resident Memory" value={ss.memory_mb?.resident != null ? `${ss.memory_mb.resident} MB` : null} mono />
          <KV label="Virtual Memory" value={ss.memory_mb?.virtual != null ? `${ss.memory_mb.virtual} MB` : null} mono />
          <KV label="Max BSON Size" value={dbStats.max_bson_object_size && formatBytes(dbStats.max_bson_object_size)} mono />
        </SectionCard>

        <SectionCard title="Database Host" testId="db-host-card" accent="bg-blue-500/10" icon={Network} iconClass="text-blue-500">
          <KV label="Hostname" value={host.hostname} mono />
          <KV label="OS Name" value={host.os_name?.replace(/^PRETTY_NAME=/, "").replace(/"/g, "")} />
          <KV label="OS Version" value={host.os_version} mono />
          <KV label="Cores" value={host.num_cores} />
          <KV label="Memory" value={host.memory_size_mb ? `${host.memory_size_mb} MB` : null} mono />
          <KV label="CPU Architecture" value={host.cpu_arch} mono />
          {ss.network && (
            <>
              <div className="pt-2 mt-1 border-t border-border/40 text-[10px] text-muted-foreground mb-1">Network</div>
              <KV label="Bytes In" value={formatBytes(ss.network.bytes_in)} mono />
              <KV label="Bytes Out" value={formatBytes(ss.network.bytes_out)} mono />
              <KV label="Total Requests" value={ss.network.num_requests?.toLocaleString()} mono />
            </>
          )}
        </SectionCard>
      </div>

      {/* Operation counters */}
      {ss.opcounters && (
        <Card className="glass-card border-theme" data-testid="opcounters-card">
          <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-purple-500/10">
            <Database className="w-4 h-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Operation Counters (since server start)</h3>
            <Badge variant="outline" className="ml-auto text-[10px]">live</Badge>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {Object.entries(ss.opcounters).map(([op, count]) => (
                <div key={op} className="bg-muted/40 rounded p-3 text-center" data-testid={`opcount-${op}`}>
                  <div className="text-base font-bold tabular-nums text-foreground">{Number(count).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{op}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Collections list */}
      <Card className="glass-card border-theme" data-testid="collections-card">
        <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-amber-500/10">
          <Layers className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Collections</h3>
          <Badge variant="outline" className="ml-2 text-[10px]">
            {dbStats.collections?.length || 0} total · {sortedCollections.length} shown
          </Badge>
          <div className="ml-auto relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Filter collections…"
              value={collFilter}
              onChange={(e) => setCollFilter(e.target.value)}
              className="pl-8 h-7 text-xs w-44"
              data-testid="collections-filter"
            />
          </div>
        </div>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 sticky top-0 z-10">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Collection</th>
                  <th className="px-4 py-2 font-medium text-right">Documents</th>
                  <th className="px-4 py-2 font-medium text-right">Avg Doc</th>
                  <th className="px-4 py-2 font-medium text-right">Data Size</th>
                  <th className="px-4 py-2 font-medium text-right">Storage</th>
                  <th className="px-4 py-2 font-medium text-right">Indexes</th>
                  <th className="px-4 py-2 font-medium text-right">Index Size</th>
                </tr>
              </thead>
              <tbody>
                {sortedCollections.map((c, i) => (
                  <tr key={c.name} className="border-t border-border/40 hover:bg-muted/20" data-testid={`collection-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-foreground">
                      {c.name}
                      {c.capped && <Badge variant="outline" className="ml-2 text-[9px]">capped</Badge>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{(c.count || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{formatBytes(c.avg_obj_size)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatBytes(c.size)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatBytes(c.storage_size)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.num_indexes ?? 0}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatBytes(c.total_index_size)}</td>
                  </tr>
                ))}
                {sortedCollections.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    {collFilter ? "No collections match your filter." : "No collections found."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseInfoPanel;
